// src/pages/contentManagement/verses/versesList.js
import React, { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";
import dayjs from "dayjs";
import { Button, Collapse, DatePicker, Divider, Form, Input, List, Modal, Pagination, Select, Space, Spin, Tag, Tooltip } from "antd";
import { FaEdit, FaTrash, FaChevronDown, FaInfoCircle } from "react-icons/fa";
import axios from "../../../services/axiosConfig";
import API_ENDPOINTS from "../../../services/apiEndpoints";
import { getAuthToken } from "../../../auth/auth";
import useNotification from "../../../hooks/useNotification";
import Layout from "../../../components/layout/layout";
import TableLayout from "../../../components/tableLayout/tableLayout";
import TopToolBar from "../../../components/topToolBar/topToolBar";
import {
  buildBookIndexMap,
  getAllBooks,
  getAvailableBibleVersions,
  getBookNameByIndex,
  getCachedBibleData,
  getChaptersForBook,
  getVerseByReference,
  getVersesForChapter,
  searchBible
} from "../../../utils/bible/service";
import "./versesList.css";

const { Search } = Input;
const { Option } = Select;

// ─── Phase constants (the state machine) ─────────────────────────────────────
const PHASES = {
  LOADING_VERSIONS: "LOADING_VERSIONS", // Fetching version list + raw verses in parallel
  AWAITING_VERSION: "AWAITING_VERSION", // Versions ready, user must pick one
  LOADING_BIBLE: "LOADING_BIBLE", // Version known, loading Bible from cache/network
  HYDRATING: "HYDRATING", // Bible ready, resolving verse texts
  READY: "READY", // Everything loaded — table renders
  ERROR: "ERROR" // Unrecoverable failure
};

// ─── Reducer ──────────────────────────────────────────────────────────────────
const initialState = {
  // Phase
  phase: PHASES.LOADING_VERSIONS,
  errorMessage: null,

  // Data
  versions: [],
  version: null,
  bible: null,
  rawVerses: [], // Fetched from backend, no text yet
  selectedVerses: [], // Hydrated — has .text resolved from Bible

  // Form dropdowns
  availableBooks: [],
  availableChapters: [],
  availableVerses: [],

  // Table controls
  searchTerm: "",
  filters: {},
  currentPage: 1,
  pageSize: 5,
  sortConfig: { key: "createdAt", direction: "desc" },

  // Delete modal
  showDeleteModal: false,
  deleteVerseId: null,

  // Edit target
  editingVerse: null
};

const reducer = (state, action) => {
  switch (action.type) {
    // ── Phase transitions ────────────────────────────────────────────────────
    case "SET_PHASE":
      return { ...state, phase: action.payload, errorMessage: null };

    case "SET_ERROR":
      return { ...state, phase: PHASES.ERROR, errorMessage: action.payload };

    // ── Bootstrap data ───────────────────────────────────────────────────────
    case "BOOTSTRAP_READY": {
      // Versions loaded + raw verses fetched. Decide next phase.
      const savedVersion = localStorage.getItem("defaultBibleVersion");
      const versionExists = action.payload.versions.find((v) => v.versionId === savedVersion);
      const version = versionExists ? savedVersion : null;
      return {
        ...state,
        versions: action.payload.versions,
        rawVerses: action.payload.rawVerses,
        version,
        phase: version ? PHASES.LOADING_BIBLE : PHASES.AWAITING_VERSION
      };
    }

    case "SET_VERSION": {
      // User picked a version from the selector
      localStorage.setItem("defaultBibleVersion", action.payload);
      return {
        ...state,
        version: action.payload,
        bible: null,
        availableBooks: [],
        availableChapters: [],
        availableVerses: [],
        phase: PHASES.LOADING_BIBLE
      };
    }

    // ── Bible + hydration ─────────────────────────────────────────────────────
    case "BIBLE_LOADED":
      return {
        ...state,
        bible: action.payload,
        phase: PHASES.HYDRATING
      };

    case "HYDRATION_COMPLETE":
      return {
        ...state,
        selectedVerses: action.payload,
        phase: PHASES.READY
      };

    // ── Books / chapters / verses dropdowns ──────────────────────────────────
    case "SET_BOOKS":
      return { ...state, availableBooks: action.payload };

    case "SET_CHAPTERS":
      return { ...state, availableChapters: action.payload };

    case "SET_VERSES":
      return { ...state, availableVerses: action.payload };

    // ── Table controls ───────────────────────────────────────────────────────
    case "SET_SEARCH":
      return { ...state, searchTerm: action.payload, currentPage: 1 };

    case "SET_FILTERS":
      return { ...state, filters: action.payload, currentPage: 1 };

    case "RESET_FILTERS":
      return { ...state, filters: {}, searchTerm: "", currentPage: 1 };

    case "SET_PAGINATION":
      return {
        ...state,
        currentPage: action.payload.page,
        pageSize: action.payload.pageSize
      };

    // ── CRUD on selectedVerses ───────────────────────────────────────────────
    case "ADD_VERSE":
      return {
        ...state,
        selectedVerses: [action.payload, ...state.selectedVerses]
      };

    case "UPDATE_VERSE":
      return {
        ...state,
        selectedVerses: state.selectedVerses.map((v) => (v.id === action.payload.id ? action.payload : v))
      };

    case "REMOVE_VERSE":
      return {
        ...state,
        selectedVerses: state.selectedVerses.filter((v) => v.id !== action.payload)
      };

    // ── Delete modal ─────────────────────────────────────────────────────────
    case "SHOW_DELETE_MODAL":
      return { ...state, showDeleteModal: true, deleteVerseId: action.payload };

    case "HIDE_DELETE_MODAL":
      return { ...state, showDeleteModal: false, deleteVerseId: null };

    // ── Edit ─────────────────────────────────────────────────────────────────
    case "SET_EDITING_VERSE":
      return { ...state, editingVerse: action.payload };

    default:
      return state;
  }
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

// Parse stored reference "1 1:1" → { bookIndex, chapter, verse }
function parseReference(reference) {
  if (!reference) return null;
  const match = reference.match(/^(\d+)\s+(\d+):(\d+)$/);
  if (!match) return null;
  return {
    bookIndex: Number(match[1]),
    chapter: match[2],
    verse: match[3]
  };
}

// ─── DisplayReference ────────────────────────────────────────────────────────
function DisplayReference({ reference, bible, version }) {
  const [display, setDisplay] = React.useState(reference);

  React.useEffect(() => {
    if (!bible || !reference) {
      setDisplay(reference);
      return;
    }
    const parsed = parseReference(reference);
    if (!parsed) {
      setDisplay(reference);
      return;
    }
    try {
      const bookIndexMap = buildBookIndexMap(bible);
      const bookName = getBookNameByIndex(bookIndexMap, parsed.bookIndex);
      setDisplay(bookName ? `${bookName} ${parsed.chapter}:${parsed.verse}` : reference);
    } catch (err) {
      console.error("[DisplayReference] Could not resolve book name:", err);
      setDisplay(reference);
    }
  }, [reference, bible, version]);

  return display;
}

// ─── Simple centred spinner used for LOADING_BIBLE and HYDRATING ─────────────
function TableSpinner({ tip }) {
  return (
    <div style={{ textAlign: "center", padding: "60px 0" }}>
      <Spin size="large" />
      {tip && <div style={{ marginTop: 16, color: "hsl(210,100%,45%)", fontWeight: 500 }}>{tip}</div>}
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
function VerseManagement() {
  const { notification, showNotification, hideNotification } = useNotification();
  const [state, dispatch] = useReducer(reducer, initialState);
  const [form] = Form.useForm();

  // ── Form-local state (belongs in the form, not the page state machine) ─────
  const [book, setBook] = useState("");
  const [chapter, setChapter] = useState("");
  const [verse, setVerse] = useState("");
  const [text, setText] = useState("");
  const [assignedDate, setAssignedDate] = useState(null);
  const [searchInputValue, setSearchInputValue] = useState("");
  const [results, setResults] = useState([]);

  // ── Loading flags for form interactions only ──────────────────────────────
  const [loadingChapters, setLoadingChapters] = useState(false);
  const [loadingVerses, setLoadingVerses] = useState(false);
  const [loadingVerseText, setLoadingVerseText] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);

  // ── Destructure state ─────────────────────────────────────────────────────
  const {
    phase,
    errorMessage,
    versions,
    version,
    bible,
    selectedVerses,
    availableBooks,
    availableChapters,
    availableVerses,
    searchTerm,
    filters,
    currentPage,
    pageSize,
    showDeleteModal,
    deleteVerseId,
    editingVerse,
    sortConfig
  } = state;

  // ─── Phase-gated derived booleans ─────────────────────────────────────────
  const isBootstrapping = phase === PHASES.LOADING_VERSIONS;
  const isAwaitingVersion = phase === PHASES.AWAITING_VERSION;
  const isLoadingBible = phase === PHASES.LOADING_BIBLE;
  const isHydrating = phase === PHASES.HYDRATING;
  const isReady = phase === PHASES.READY;
  const isError = phase === PHASES.ERROR;

  // Table should show a spinner instead of the list when not ready
  const tableIsLoading = isLoadingBible || isHydrating;

  // Form controls should be disabled until we have a loaded Bible
  const formDisabled = !isReady && !isAwaitingVersion;

  // ─── THE ORCHESTRATOR ─────────────────────────────────────────────────────
  // One effect, explicit sequencing. Each phase hands off to the next.
  // Nothing fires unless the phase is right.

  const orchestratorRef = useRef(false); // prevent double-fire in StrictMode

  useEffect(() => {
    if (orchestratorRef.current) return;
    orchestratorRef.current = true;

    async function bootstrap() {
      // ── PHASE 1: Load versions list + raw verse list in parallel ──────────
      try {
        const token = await getAuthToken();

        const [versionsResult, versesResult] = await Promise.allSettled([
          // Versions
          axios
            .get(API_ENDPOINTS.BIBLE_VERSIONS, {
              headers: { Authorization: `Bearer ${token}` }
            })
            .then((r) => {
              const all = r.data?.data || [];
              return all.filter((v) => v.visible === true);
            })
            .catch(async () => {
              // Fallback to cache util
              const fallback = await getAvailableBibleVersions();
              return fallback || [];
            }),

          // Raw verses (no hydration yet)
          axios
            .get(API_ENDPOINTS.VERSE_OF_THE_DAY, {
              headers: { Authorization: `Bearer ${token}` }
            })
            .then((r) => r.data?.data || r.data || [])
            .catch(() => [])
        ]);

        const versions = versionsResult.status === "fulfilled" ? versionsResult.value : [];
        const rawVerses = versesResult.status === "fulfilled" ? versesResult.value : [];

        if (versions.length === 0) {
          dispatch({
            type: "SET_ERROR",
            payload: "No Bible versions available. Please contact support."
          });
          return;
        }

        // BOOTSTRAP_READY decides the next phase (AWAITING_VERSION or LOADING_BIBLE)
        dispatch({ type: "BOOTSTRAP_READY", payload: { versions, rawVerses } });
      } catch (err) {
        console.error("[orchestrator] bootstrap failed:", err);
        dispatch({
          type: "SET_ERROR",
          payload: "Failed to load page data. Please refresh."
        });
      }
    }

    bootstrap();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── PHASE 3: Load Bible when version is set ────────────────────────────────
  useEffect(() => {
    if (phase !== PHASES.LOADING_BIBLE || !version) return;

    let cancelled = false;

    async function loadBible() {
      try {
        const bibleData = await getCachedBibleData(version);

        if (cancelled) return;

        if (!bibleData?.verses?.length) {
          showNotification("Bible data unavailable for this version. Try a different version.", "warning");
          // Recover to READY with empty text rather than crashing
          dispatch({ type: "HYDRATION_COMPLETE", payload: state.rawVerses });
          return;
        }

        dispatch({ type: "BIBLE_LOADED", payload: bibleData });
      } catch (err) {
        if (cancelled) return;
        console.error("[orchestrator] loadBible failed:", err);
        dispatch({
          type: "SET_ERROR",
          payload: "Failed to load Bible data. Please try again."
        });
      }
    }

    loadBible();
    return () => {
      cancelled = true;
    };
  }, [phase, version]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── PHASE 4: Hydrate verse texts once Bible is ready ──────────────────────
  useEffect(() => {
    if (phase !== PHASES.HYDRATING || !bible) return;

    let cancelled = false;

    async function hydrate() {
      const rawVerses = state.rawVerses;

      if (!rawVerses?.length) {
        if (!cancelled) dispatch({ type: "HYDRATION_COMPLETE", payload: [] });
        return;
      }

      // Build the index→name map once from the loaded Bible data.
      // This replaces the per-verse array scan that was happening before.
      let bookIndexMap;
      try {
        bookIndexMap = buildBookIndexMap(bible);
      } catch (err) {
        console.error("[hydrate] Failed to build book index map:", err);
        // Still try to hydrate — getBookNameByIndex will warn and fall back
        bookIndexMap = new Map();
      }

      if (bookIndexMap.size === 0) {
        console.warn("[hydrate] Book index map is empty — verse texts may not resolve correctly.");
      }

      let failCount = 0;

      const hydrated = await Promise.all(
        rawVerses.map(async (v) => {
          // Guard: skip malformed verse records
          if (!v || typeof v !== "object") {
            failCount++;
            return v;
          }

          const parsed = parseReference(v.reference);
          if (!parsed) {
            // Reference string is in an unexpected format — pass through as-is
            return v;
          }

          try {
            // O(1) lookup — no verse array scan
            const bookName = getBookNameByIndex(bookIndexMap, parsed.bookIndex);

            if (!bookName) {
              console.warn(`[hydrate] Could not resolve book index ${parsed.bookIndex} ` 
                + `for reference "${v.reference}".`);
              failCount++;
              return { ...v, text: "" };
            }

            const verseText = await getVerseByReference(bible, bookName, parsed.chapter, parsed.verse, version);

            if (!verseText) {
              console.warn(`[hydrate] Empty text for ${bookName} ${parsed.chapter}:${parsed.verse}.`);
              failCount++;
            }

            return { ...v, text: verseText || "" };
          } catch (err) {
            console.error(`[hydrate] Unexpected error resolving "${v.reference}":`, err);
            failCount++;
            return { ...v, text: "" };
          }
        })
      );

      if (cancelled) return;

      if (failCount > 0) {
        showNotification(`${failCount} verse(s) couldn't be resolved in this version.`, "warning");
      }

      dispatch({ type: "HYDRATION_COMPLETE", payload: hydrated });
    }

    hydrate();
    return () => {
      cancelled = true;
    };
  }, [phase, bible, version]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Load books when Bible becomes available ────────────────────────────────
  useEffect(() => {
    if (!bible || !version) return;
    getAllBooks(bible, version).then((books) => {
      dispatch({ type: "SET_BOOKS", payload: books });
    });
  }, [bible, version]);

  // ── Load chapters when book changes ───────────────────────────────────────
  useEffect(() => {
    if (!bible || !book || !version) {
      dispatch({ type: "SET_CHAPTERS", payload: [] });
      return;
    }
    setLoadingChapters(true);
    getChaptersForBook(bible, book, version)
      .then((chapters) => dispatch({ type: "SET_CHAPTERS", payload: chapters }))
      .catch(() => dispatch({ type: "SET_CHAPTERS", payload: [] }))
      .finally(() => setLoadingChapters(false));
  }, [bible, book, version]);

  // ── Load verses when chapter changes ──────────────────────────────────────
  useEffect(() => {
    if (!bible || !book || !chapter || !version) {
      dispatch({ type: "SET_VERSES", payload: [] });
      return;
    }
    setLoadingVerses(true);
    getVersesForChapter(bible, book, chapter, version)
      .then((verses) => dispatch({ type: "SET_VERSES", payload: verses }))
      .catch(() => dispatch({ type: "SET_VERSES", payload: [] }))
      .finally(() => setLoadingVerses(false));
  }, [bible, book, chapter, version]);

  // ── Load verse text when verse changes ────────────────────────────────────
  useEffect(() => {
    if (!bible || !book || !chapter || !verse || !version) {
      setText("");
      return;
    }
    setLoadingVerseText(true);
    getVerseByReference(bible, book, chapter, verse, version)
      .then((t) => {
        setText(t || "");
        if (!t) showNotification("Verse text not found", "warning");
      })
      .catch(() => {
        setText("");
        showNotification("Failed to load verse text", "error");
      })
      .finally(() => setLoadingVerseText(false));
  }, [bible, book, chapter, verse, version]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Filter / sort / paginate (pure derivation, no effects) ──────────────
  const filteredData = useMemo(() => {
    let result = [...selectedVerses];

    if (filters.sortOrder === "newest") {
      result.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
    } else if (filters.sortOrder === "oldest") {
      result.sort((a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0));
    } else if (sortConfig) {
      result.sort((a, b) => {
        const { key, direction } = sortConfig;
        if (key === "createdAt") {
          return direction === "asc"
            ? new Date(a.createdAt || 0) - new Date(b.createdAt || 0)
            : new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
        }
        const valA = a[key]?.toString().toLowerCase() || "";
        const valB = b[key]?.toString().toLowerCase() || "";
        if (valA < valB) return direction === "asc" ? -1 : 1;
        if (valA > valB) return direction === "asc" ? 1 : -1;
        return 0;
      });
    }

    if (searchTerm?.trim()) {
      const q = searchTerm.toLowerCase().trim();
      result = result.filter((item) =>
        [item.reference || "", item.text || "", item.assignedDate || ""].some((f) => f.toLowerCase().includes(q))
      );
    }

    return result;
  }, [selectedVerses, filters, searchTerm, sortConfig]);

  const currentPageData = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredData.slice(start, start + pageSize);
  }, [filteredData, currentPage, pageSize]);

  // ─── Bible search ─────────────────────────────────────────────────────────
  const handleBibleSearch = useCallback(
    async (term) => {
      if (!term?.trim() || term.length < 2) {
        setResults([]);
        return;
      }
      if (!bible?.verses?.length) {
        showNotification("Bible not loaded yet. Please wait.", "warning");
        return;
      }
      setSearchLoading(true);
      try {
        // Exact word matching
        const searchPattern = new RegExp(`\\b${term.trim()}\\b`, "i");
        const isReference = /^\w+\s+\d+:?\d*$/.test(term.trim());

        let found;
        if (isReference) {
          found = await searchBible(bible, term, version);
        } else {
          found = bible.verses
            .filter((v) => v.text && searchPattern.test(v.text))
            .slice(0, 100)
            .map((v) => {
              const bookName = v.book_name || v.book || "";
              return {
                book: bookName,
                chapter: v.chapter,
                verse: v.verse,
                text: v.text,
                fullReference: `${bookName} ${v.chapter}:${v.verse}`
              };
            });
        }

        setResults(found);
        if (!found.length && term.length >= 3) {
          showNotification("No verses found matching your search", "info");
        }
      } catch {
        setResults([]);
        showNotification("Search failed. Please try again.", "error");
      } finally {
        setSearchLoading(false);
      }
    },
    [bible, version, showNotification]
  );

  // ─── Form helpers ─────────────────────────────────────────────────────────
  const clearForm = useCallback(() => {
    setBook("");
    setChapter("");
    setVerse("");
    setText("");
    setResults([]);
    setSearchInputValue("");
    setAssignedDate(null);
    dispatch({ type: "SET_EDITING_VERSE", payload: null });
    form.resetFields();
  }, [form]);

  // ─── Save / Update ────────────────────────────────────────────────────────
  const handleSave = useCallback(async () => {
    if (!book || !chapter || !verse) {
      showNotification("Please select book, chapter, and verse", "error");
      return;
    }
    if (!text?.trim()) {
      showNotification("Verse text is empty. Please wait for it to load.", "error");
      return;
    }

    setSubmitLoading(true);
    try {
      const token = await getAuthToken();

      const sampleVerse = bible?.verses?.find((v) => v.book_name === book || v.book === book);
      const bookIndex = sampleVerse ? Number(sampleVerse.book) : null;
      if (!bookIndex || isNaN(bookIndex)) {
        showNotification("Could not resolve book index. Please try again.", "error");
        return;
      }

      const verseRef = `${bookIndex} ${chapter}:${verse}`;
      const payload = { reference: verseRef };
      if (assignedDate) payload.assignedDate = assignedDate.format("YYYY-MM-DD");

      let response;
      if (editingVerse?.id) {
        response = await axios.patch(`${API_ENDPOINTS.VERSE_OF_THE_DAY}/${editingVerse.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        response = await axios.post(API_ENDPOINTS.VERSE_OF_THE_DAY, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }

      if (response.status === 200 || response.status === 201) {
        const savedVerse = response.data?.verse || response.data?.data || response.data;
        const hydratedVerse = { ...savedVerse, text };

        if (editingVerse?.id) {
          dispatch({ type: "UPDATE_VERSE", payload: hydratedVerse });
          showNotification("Verse updated successfully!", "success");
        } else {
          dispatch({ type: "ADD_VERSE", payload: hydratedVerse });
          showNotification(assignedDate ? `Verse saved for ${assignedDate.format("YYYY-MM-DD")}!` : "Verse saved successfully!", "success");
        }
        clearForm();
      }
    } catch (error) {
      console.error("[handleSave]", error);
      const status = error.response?.status;
      if (status === 401) showNotification("Authentication failed. Please login again.", "error");
      else if (status === 403) showNotification("Access denied. Admin privileges required.", "error");
      else showNotification(error.response?.data?.message || "Failed to save verse.", "error");
    } finally {
      setSubmitLoading(false);
    }
  }, [book, chapter, verse, text, assignedDate, editingVerse, bible, showNotification, clearForm]);

  // ─── Edit ─────────────────────────────────────────────────────────────────
  const handleEdit = useCallback(
    (verseItem) => {
      dispatch({ type: "SET_EDITING_VERSE", payload: verseItem });

      const parsed = parseReference(verseItem.reference);
      if (parsed && bible) {
        // Build the map on demand for this single edit interaction.
        // Cost is one pass through the verse array — acceptable for a user action.
        try {
          const bookIndexMap = buildBookIndexMap(bible);
          const bookName = getBookNameByIndex(bookIndexMap, parsed.bookIndex);
          if (bookName) setBook(bookName);
        } catch (err) {
          console.error("[handleEdit] Could not resolve book name:", err);
        }
        setChapter(parsed.chapter);
        setVerse(parsed.verse);
      }

      setText(verseItem.text || "");

      const parsedDate = verseItem.assignedDate ? dayjs(verseItem.assignedDate) : null;
      setAssignedDate(parsedDate);
      form.setFieldsValue({ assignedDate: parsedDate });

      window.scrollTo({ top: 0, behavior: "smooth" });
    },
    [bible, form]
  );

  const handleCancel = useCallback(() => {
    clearForm();
    showNotification("Form cleared.", "info");
  }, [clearForm, showNotification]);

  // ─── Delete ───────────────────────────────────────────────────────────────
  const confirmDelete = useCallback((verseId) => {
    dispatch({ type: "SHOW_DELETE_MODAL", payload: verseId });
  }, []);

  const handleDelete = useCallback(async () => {
    if (!deleteVerseId) return;
    try {
      const token = await getAuthToken();
      await axios.delete(`${API_ENDPOINTS.VERSE_OF_THE_DAY}/${deleteVerseId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      dispatch({ type: "HIDE_DELETE_MODAL" });
      dispatch({ type: "REMOVE_VERSE", payload: deleteVerseId });
      showNotification("Verse deleted successfully.", "success");
    } catch {
      showNotification("Failed to delete verse. Please try again.", "error");
    }
  }, [deleteVerseId, showNotification]);

  // ─── Toolbar handlers ─────────────────────────────────────────────────────
  const handleVersionChange = useCallback((newVersion) => {
    dispatch({ type: "SET_VERSION", payload: newVersion });
    // Reset form selectors
    setBook("");
    setChapter("");
    setVerse("");
    setText("");
    setResults([]);
    setSearchInputValue("");
  }, []);

  const handleBookChange = useCallback((v) => {
    setBook(v);
    setChapter("");
    setVerse("");
    setText("");
  }, []);
  const handleChapterChange = useCallback((v) => {
    setChapter(v);
    setVerse("");
    setText("");
  }, []);
  const handleVerseChange = useCallback((v) => {
    setVerse(v);
  }, []);

  const filtersConfig = useMemo(
    () => ({
      sortOrder: {
        label: "Sort By",
        options: [
          { value: "newest", label: "Newest First" },
          { value: "oldest", label: "Oldest First" }
        ]
      }
    }),
    []
  );

  const prepareExcelData = useCallback(
    () =>
      filteredData.map((item, index) => ({
        "SL. NO.": index + 1,
        Reference: item.reference,
        Text: item.text || "",
        "Assigned Date": item.assignedDate || "Not Assigned"
      })),
    [filteredData]
  );

  // ─── Render: full-page spinner while bootstrapping ────────────────────────
  if (isBootstrapping) {
    return (
      <Layout
        navTitle="Content Management"
        pageTitle="Verses"
        notificationProps={{
          show: notification.show,
          message: notification.message,
          type: notification.type,
          onClose: hideNotification,
          duration: 4000
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "60vh"
          }}
        >
          <Spin size="large" tip="Loading..." />
        </div>
      </Layout>
    );
  }

  // ─── Render: unrecoverable error ──────────────────────────────────────────
  if (isError) {
    return (
      <Layout
        navTitle="Content Management"
        pageTitle="Verses"
        notificationProps={{
          show: notification.show,
          message: notification.message,
          type: notification.type,
          onClose: hideNotification,
          duration: 4000
        }}
      >
        <div
          style={{
            textAlign: "center",
            padding: "60px 20px",
            color: "hsl(0,80%,45%)"
          }}
        >
          <p style={{ fontSize: 16, fontWeight: 500 }}>{errorMessage}</p>
          <Button onClick={() => window.location.reload()}>Retry</Button>
        </div>
      </Layout>
    );
  }

  // ─── Render: main page ────────────────────────────────────────────────────
  return (
    <Layout
      navTitle="Content Management"
      pageTitle="Verses"
      notificationProps={[
        {
          show: notification.show,
          message: notification.message,
          type: notification.type,
          onClose: hideNotification,
          duration: 4000
        }
      ]}
    >
      {/* ── Bible Version Panel ── */}
      <div className="votd-version-panel">
        <h3 className="votd-version-panel-title">
          Bible Versions
          <Tooltip
            title="Preferred version not found? Head to the Bible Versions page to enable the versions you need."
            placement="right"
            styles={{ body: { width: "40em" } }}
          >
            <FaInfoCircle
              style={{
                fontSize: "12px",
                color: "hsl(0, 0%, 50%)",
                margin: "0 0 8px 4px"
              }}
            />
          </Tooltip>
        </h3>
        <div className="votd-version-panel-body">
          <Select
            value={version}
            onChange={handleVersionChange}
            className="votd-version-select"
            placeholder="Select a Bible version to get started"
            disabled={isLoadingBible || isHydrating}
          >
            {versions.map((v) => (
              <Option key={v.versionId} value={v.versionId}>
                {v.title || v.versionName || v.versionId}
              </Option>
            ))}
          </Select>

          {isAwaitingVersion && (
            <div className="votd-version-hint">👆 Select a Bible version to enable verse browsing and display verse texts.</div>
          )}
        </div>
      </div>

      {/* ── Add / Edit Form ── */}
      <div className="votd-upload-section">
        <Collapse
          expandIcon={({ isActive }) => (
            <FaChevronDown
              style={{
                transform: isActive ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 0.3s"
              }}
            />
          )}
          className="votd-upload-collapse"
          activeKey={editingVerse ? ["1"] : undefined}
          items={[
            {
              key: "1",
              label: editingVerse ? "Edit Verse" : "Add New Verse",
              children: (
                <Form form={form} layout="vertical" className="votd-upload-form">
                  {/* Edit mode banner */}
                  {editingVerse && (
                    <div className="votd-edit-banner">
                      ✏️ Editing: <strong>{editingVerse.reference}</strong>
                      {" — "}select a new verse below and/or change the date, then click <strong>Update Verse</strong>.
                    </div>
                  )}

                  {/* ── Verse Selection ── */}
                  <div className="votd-form-section">
                    <h3 className="votd-section-title">Verse Selection</h3>

                    {/* Search */}
                    <Form.Item label="Search Bible:" name="search">
                      <div>
                        {version && bible && (
                          <div className="votd-search-meta">
                            Searching in: <strong>{versions.find((v) => v.versionId === version)?.title || version}</strong> (
                            {bible.verses?.length || 0} verses)
                            <span className="votd-exact-badge">✓ Exact word matching</span>
                          </div>
                        )}
                        <Search
                          placeholder={
                            version ? `Search ${versions.find((v) => v.versionId === version)?.title || version}` : "Select a version first"
                          }
                          value={searchInputValue}
                          loading={searchLoading}
                          onChange={(e) => setSearchInputValue(e.target.value)}
                          onSearch={handleBibleSearch}
                          allowClear
                          onClear={() => {
                            setSearchInputValue("");
                            setResults([]);
                          }}
                          className="votd-upload-input"
                          disabled={formDisabled || !bible}
                          enterButton="Search"
                        />
                      </div>
                    </Form.Item>

                    {searchLoading && (
                      <div className="votd-search-loading">
                        <Spin tip="Searching Bible…" />
                      </div>
                    )}

                    {!searchLoading && results.length > 0 && (
                      <div className="votd-search-results">
                        <div className="votd-search-results-title">
                          Search Results ({results.length}
                          {results.length === 100 ? "+" : ""})
                        </div>
                        <div className="votd-search-results-list">
                          {results.map((r, idx) => {
                            const isSelected = book === r.book && chapter === r.chapter && verse === r.verse;
                            const isInList = selectedVerses.some(
                              (sv) => sv.book === r.book && sv.chapter === r.chapter && sv.verse === r.verse && sv.version === version
                            );
                            return (
                              <div
                                key={`${r.fullReference}-${idx}`}
                                className={`votd-search-result-item ${isSelected ? "votd-selected" : ""} ${isInList ? "votd-in-list" : ""}`}
                                onClick={() => {
                                  setBook(r.book);
                                  setChapter(r.chapter);
                                  setVerse(r.verse);
                                  if (r.text) setText(r.text);
                                }}
                              >
                                <div className="votd-result-content">
                                  <Space>
                                    <strong>{r.fullReference}</strong>
                                    {isSelected && <Tag color="blue">Selected</Tag>}
                                    {isInList && <Tag color="green">In List</Tag>}
                                  </Space>
                                  <div className="votd-result-text">{r.text}</div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    <Divider size="large" variant="dashed" style={{ borderColor: "hsl(79,95%,36%)" }}>
                      OR
                    </Divider>

                    {/* Book / Chapter / Verse selectors */}
                    <Form.Item label="Select Book, Chapter, and Verse:" name="selection">
                      <div className="votd-selectors">
                        <Select
                          placeholder={formDisabled ? "Loading Bible…" : !bible ? "Bible data not available" : "Book"}
                          value={book || undefined}
                          onChange={handleBookChange}
                          className="votd-upload-input"
                          disabled={formDisabled || !bible}
                          showSearch
                          filterOption={(input, option) => option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
                        >
                          {availableBooks.map((b) => (
                            <Option key={b} value={b}>
                              {b}
                            </Option>
                          ))}
                        </Select>

                        <Select
                          placeholder={loadingChapters ? "Loading chapters…" : !book ? "Select book first" : "Chapter"}
                          value={chapter || undefined}
                          onChange={handleChapterChange}
                          className="votd-upload-input"
                          disabled={formDisabled || !bible || !book || loadingChapters}
                          loading={loadingChapters}
                        >
                          {availableChapters.map((c) => (
                            <Option key={c} value={c}>
                              {c}
                            </Option>
                          ))}
                        </Select>

                        <Select
                          placeholder={loadingVerses ? "Loading verses…" : !chapter ? "Select chapter first" : "Verse"}
                          value={verse || undefined}
                          onChange={handleVerseChange}
                          className="votd-upload-input"
                          disabled={formDisabled || !bible || !book || !chapter || loadingVerses}
                          loading={loadingVerses}
                        >
                          {availableVerses.map((v) => (
                            <Option key={v} value={v}>
                              {v}
                            </Option>
                          ))}
                        </Select>
                      </div>
                    </Form.Item>

                    {/* Verse text */}
                    <Form.Item label="Verse Text:" name="text" style={{ marginTop: 16 }}>
                      <div style={{ position: "relative" }}>
                        <Input.TextArea
                          value={text}
                          rows={4}
                          readOnly
                          className="votd-upload-input"
                          placeholder={loadingVerseText ? "Loading verse text…" : "Verse text will appear here"}
                        />
                        {loadingVerseText && (
                          <div className="votd-versetext-loader">
                            <Spin size="small" /> Loading…
                          </div>
                        )}
                      </div>
                    </Form.Item>
                  </div>

                  {/* ── Date Assignment ── */}
                  <div className="votd-form-section">
                    <h3 className="votd-section-title">Date Assignment</h3>
                    <Form.Item label="Assigned Date (Optional):" name="assignedDate">
                      <DatePicker
                        value={assignedDate}
                        onChange={setAssignedDate}
                        className="votd-upload-input"
                        style={{ width: 200 }}
                        placeholder="No date"
                        allowClear
                      />
                    </Form.Item>
                  </div>

                  {/* ── Actions ── */}
                  <Form.Item>
                    <div className="votd-form-btn-group">
                      <Button className="votd-form-cancel-btn" onClick={handleCancel} disabled={submitLoading}>
                        {editingVerse ? "Cancel Edit" : "Clear"}
                      </Button>
                      <Button
                        className="votd-form-submit-btn"
                        onClick={handleSave}
                        loading={submitLoading}
                        disabled={!book || !chapter || !verse || !text || submitLoading || loadingVerseText}
                      >
                        {submitLoading ? "Saving…" : editingVerse ? "Update Verse" : "Upload Verse"}
                      </Button>
                    </div>
                  </Form.Item>
                </Form>
              )
            }
          ]}
        />
      </div>

      <Divider />

      <div className="votd-table-wrapper">
        <TableLayout
          toolbar={
            <TopToolBar
              filtersConfig={filtersConfig}
              searchPlaceholder="Search verses…"
              searchTerm={searchTerm}
              filters={filters}
              onSearch={(term) => dispatch({ type: "SET_SEARCH", payload: term })}
              onFilterChange={(f) => dispatch({ type: "SET_FILTERS", payload: f })}
              onReset={() => dispatch({ type: "RESET_FILTERS" })}
              showExcelDownload={true}
              excelData={prepareExcelData()}
              excelFilename="verse-of-the-day"
              excelSheetName="Verses"
            />
          }
        >
          {/* ── Verse Table ── */}
          {tableIsLoading ? (
            <TableSpinner tip={isHydrating ? "Resolving verse texts…" : "Loading Bible data…"} />
          ) : (
            <List
              className="tpl-list"
              dataSource={currentPageData}
              locale={{
                emptyText: isAwaitingVersion ? (
                  <div className="votd-no-version-card">
                    <div className="votd-no-version-icon">📖</div>
                    <h3 className="votd-no-version-title">Select a Bible Version to continue</h3>
                    <p className="votd-no-version-desc">
                      Verse texts cannot be displayed until a Bible version is selected. Please choose a version from the{" "}
                      <strong>Bible Version</strong> panel above.
                    </p>
                  </div>
                ) : (
                  "No verses available."
                )
              }}
              renderItem={(item) => (
                <List.Item key={item.id} className="tpl-list-item">
                  <h3 className="votd-reference">
                    <DisplayReference reference={item.reference} bible={bible} version={version} />
                  </h3>
                  <p className="votd-text">{item.text}</p>
                  {item.assignedDate && <p className="votd-assigned-date">Assigned: {item.assignedDate}</p>}
                  <div className="votd-list-btn-group">
                    <Button className="votd-list-edit-btn" icon={<FaEdit />} onClick={() => handleEdit(item)}>
                      Edit
                    </Button>
                    <Button className="votd-list-delete-btn" icon={<FaTrash />} onClick={() => confirmDelete(item.id)}>
                      Delete
                    </Button>
                  </div>
                </List.Item>
              )}
            />
          )}

          {!tableIsLoading && (
            <div className="tpl-pagination-wrapper">
              <Pagination
                className="tpl-pagination"
                current={currentPage}
                pageSize={pageSize}
                total={filteredData.length}
                onChange={(page, size) =>
                  dispatch({
                    type: "SET_PAGINATION",
                    payload: { page, pageSize: size }
                  })
                }
                showSizeChanger={false}
              />
            </div>
          )}
        </TableLayout>
      </div>

      {/* Delete Confirmation Modal */}
      <Modal
        title="Confirm Deletion"
        open={showDeleteModal}
        onCancel={() => dispatch({ type: "HIDE_DELETE_MODAL" })}
        footer={[
          <div className="votd-modal-btn-group" key="modal-buttons">
            <Button className="votd-modal-cancel-btn" onClick={() => dispatch({ type: "HIDE_DELETE_MODAL" })}>
              Cancel
            </Button>
            <Button className="votd-modal-delete-btn" onClick={handleDelete}>
              Delete
            </Button>
          </div>
        ]}
        width={400}
        centered
      >
        <p>Are you sure you want to delete this verse?</p>
      </Modal>
    </Layout>
  );
}

export default VerseManagement;
